import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaChevronLeft, FaChevronRight, FaSyncAlt } from "react-icons/fa";
import { ReactComponent as AskAiIcon } from "../../../assets/icons/ask_ai.svg";
import { ReactComponent as CopyIcon } from "../../../assets/icons/copy.svg";
import { ReactComponent as NotesIcon } from "../../../assets/icons/notes.svg";
import useReaderStore from "../../../context/readerStore";
import {
  deriveChapterLabel,
  fetchAiSummary,
  forgetAiSummary,
  listAiChapters,
  peekAiSummary,
  peekLatestCachedSummarySelection,
  rememberAiSummary,
} from "../../../services/aiService";
import {
  showComingSoonNotification,
  showErrorNotification,
  showInfoNotification,
  showOperationFailedNotification,
} from "../../../services/notificationService";
import { copyTextToClipboard } from "../../../utils/textTransfer";
import AiFeedback from "../AiFeedback";
import AiMarkdown from "../AiMarkdown";
import AiResponseActions from "../AiResponseActions";
import { createStreamPainter } from "../streamPaint";

const SUMMARY_TYPES = [
  { id: "quick", summaryMode: "quick", scope: "book" },
  { id: "detailed", summaryMode: "detailed", scope: "book" },
  {
    id: "chapter",
    summaryMode: "by_chapter",
    scope: "chapter",
    drillsIn: true,
  },
];

const scopeKeyForType = (type, chapterLabel) =>
  type?.scope === "book" ? "book" : chapterLabel || "chapter";

const resolveSummaryScope = ({
  selectedType,
  selectedChapter,
  chapterLabel,
}) => {
  const type = SUMMARY_TYPES.find((item) => item.id === selectedType);
  if (!type) return null;

  const isChapterType = selectedType === "chapter";
  if (isChapterType && !selectedChapter) return null;

  const scopeKey = isChapterType
    ? selectedChapter.id
    : scopeKeyForType(type, chapterLabel);
  const localSlot = isChapterType
    ? `chapter::${scopeKey}`
    : selectedType;

  return { type, isChapterType, scopeKey, localSlot };
};

const SummaryTab = () => {
  const { t } = useTranslation();
  const documentId = useReaderStore((state) => state.documentId);
  const currentPage = useReaderStore((state) => state.currentPage);
  const metadata = useReaderStore((state) => state.metadata);
  const toc = useReaderStore((state) => state.toc);
  const epubCurrentChapterNumber = useReaderStore(
    (state) => state.epubCurrentChapterNumber,
  );
  const chapterLabel = deriveChapterLabel(
    metadata,
    currentPage,
    epubCurrentChapterNumber,
  );

  const chapters = useMemo(
    () => listAiChapters(metadata, toc),
    [metadata, toc],
  );

  // Previously always defaulted to "quick" so remount + peekAiSummary restored
  // content. Now: only seed a type when cache already has one (no auto-fetch).
  // Don't restore chapter drill-down (list scroll position isn't preserved).
  const [selectedType, setSelectedType] = useState(() => {
    return peekLatestCachedSummarySelection(documentId)?.type ?? null;
  });
  const [isChapterDrill, setIsChapterDrill] = useState(false);
  const [selectedChapterId, setSelectedChapterId] = useState(() => {
    const cached = peekLatestCachedSummarySelection(documentId);
    return cached?.type === "chapter" ? cached.scopeKey : null;
  });
  const [summaries, setSummaries] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isStreamPainting, setIsStreamPainting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [reloadToken, setReloadToken] = useState(0);
  const requestIdRef = useRef(0);
  // Last good text for the in-flight regenerate (restored on failure).
  const previousSummaryRef = useRef("");

  const selectedChapter = useMemo(
    () => chapters.find((ch) => ch.id === selectedChapterId) || null,
    [chapters, selectedChapterId],
  );

  const resolved = useMemo(
    () =>
      resolveSummaryScope({
        selectedType,
        selectedChapter,
        chapterLabel,
      }),
    [selectedType, selectedChapter, chapterLabel],
  );

  const summaryCacheSlot = resolved?.localSlot || null;

  useEffect(() => {
    if (!resolved) {
      setIsLoading(false);
      setErrorMessage("");
      return undefined;
    }

    const { type, isChapterType, scopeKey, localSlot } = resolved;
    const cached = peekAiSummary(documentId, selectedType, scopeKey);

    if (cached) {
      setSummaries((prev) => ({ ...prev, [localSlot]: cached }));
      setErrorMessage("");
      setIsLoading(false);
      previousSummaryRef.current = "";
      return undefined;
    }

    let cancelled = false;
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    const abortController = new AbortController();
    setIsLoading(true);
    setIsStreamPainting(false);
    setErrorMessage("");

    const takePreviousSummary = () => {
      const previous = previousSummaryRef.current;
      previousSummaryRef.current = "";
      return previous;
    };

    const restorePreviousSummary = (previous) => {
      if (!previous) return;
      rememberAiSummary(documentId, selectedType, previous, scopeKey);
      setSummaries((prev) => ({ ...prev, [localSlot]: previous }));
    };

    const painter = createStreamPainter((visible, isDone) => {
      if (cancelled || requestIdRef.current !== requestId) return;
      setIsStreamPainting(!isDone);
      setSummaries((prev) => ({
        ...prev,
        [localSlot]: visible,
      }));
    });

    const loadSummary = async () => {
      try {
        const nextAnswer = await fetchAiSummary({
          documentId,
          type: selectedType,
          scopeKey,
          summaryMode: type.summaryMode || "quick",
          summarizeScope: type.scope || "book",
          signal: abortController.signal,
          onUpdate: (partial) => {
            if (cancelled || requestIdRef.current !== requestId) return;
            const text = partial?.answer || "";
            if (!text && !partial?.done) return;
            painter.push(text, Boolean(partial?.done));
          },
          ...(isChapterType
            ? {
                chapter: selectedChapter.contextChapter,
                pageNumber: selectedChapter.startPage || undefined,
              }
            : {}),
        });
        if (cancelled || requestIdRef.current !== requestId) {
          restorePreviousSummary(takePreviousSummary());
          return;
        }
        previousSummaryRef.current = "";
        painter.push(nextAnswer || "", true);
        setSummaries((prev) => ({
          ...prev,
          [localSlot]: nextAnswer,
        }));
      } catch (error) {
        painter.cancel();
        const previous = takePreviousSummary();
        restorePreviousSummary(previous);
        if (cancelled || requestIdRef.current !== requestId) return;
        if (error?.code === "ABORTED") return;

        const message = error?.message || "";
        if (previous) {
          showErrorNotification(
            t("readingAssistant.summary.error", { message }),
          );
        } else {
          setSummaries((prev) => ({ ...prev, [localSlot]: "" }));
          setErrorMessage(message);
        }
      } finally {
        if (!cancelled && requestIdRef.current === requestId) {
          setIsLoading(false);
        }
      }
    };

    loadSummary();
    return () => {
      cancelled = true;
      abortController.abort();
      painter.cancel();
      setIsStreamPainting(false);
    };
  }, [
    selectedType,
    documentId,
    chapterLabel,
    selectedChapterId,
    selectedChapter,
    resolved,
    reloadToken,
    t,
  ]);

  const answer =
    (summaryCacheSlot && summaries[summaryCacheSlot]) ||
    (selectedType !== "chapter" ? summaries[selectedType] : "") ||
    "";

  const handleSelectType = (id) => {
    if (id === "chapter") {
      // Drill in without clearing the current summary until a chapter is chosen.
      setIsChapterDrill(true);
      return;
    }
    setSelectedType(id);
    setIsChapterDrill(false);
  };

  const handleBackToTypes = () => {
    setIsChapterDrill(false);
  };

  const handleSelectChapter = (chapter) => {
    setSelectedType("chapter");
    setSelectedChapterId(chapter.id);
  };

  const handleCopy = async () => {
    if (!answer || isLoading) return;
    try {
      await copyTextToClipboard(answer);
      showInfoNotification("publicationDetails.copied");
    } catch (error) {
      showOperationFailedNotification(t("notifications.operations.copyText"));
    }
  };

  const handleRegenerate = () => {
    if (!resolved || isLoading) return;
    previousSummaryRef.current = answer || "";
    forgetAiSummary(documentId, selectedType, resolved.scopeKey);
    setErrorMessage("");
    setReloadToken((token) => token + 1);
  };

  const hasSelectedType = Boolean(selectedType);

  const cardTitle =
    selectedType === "chapter" && selectedChapter
      ? selectedChapter.label
      : hasSelectedType
        ? t(`readingAssistant.summary.cardTitles.${selectedType}`)
        : t("readingAssistant.summary.welcomeTitle");

  const emptyCopy =
    !hasSelectedType
      ? t("readingAssistant.summary.welcome")
      : selectedType === "chapter" && !selectedChapter
        ? t("readingAssistant.summary.chooseChapter")
        : t("readingAssistant.summary.empty");

  const showActions =
    Boolean(resolved) && (Boolean(answer) || Boolean(errorMessage));

  return (
    <div className="reading-assistant-tab-panel summary-tab">
      <div className="summary-tab-body">
        <div className="summary-result" aria-live="polite">
          <AskAiIcon
            className="summary-persona preserve-original-fill"
            aria-hidden="true"
          />
          <div className="summary-result-main">
            <article className="summary-card">
              <h4 className="summary-card-title">{cardTitle}</h4>
              <div className="summary-card-content">
                <AiFeedback
                  isLoading={isLoading && !answer}
                  errorMessage={answer ? "" : errorMessage}
                  loadingKey="readingAssistant.summary.loading"
                  errorKey="readingAssistant.summary.error"
                />
                {!isLoading && !errorMessage && !answer && (
                  <p
                    className={
                      !hasSelectedType ? "summary-welcome-copy" : undefined
                    }
                  >
                    {emptyCopy}
                  </p>
                )}
                {answer ? (
                  <div
                    className={
                      isStreamPainting ? "ai-stream-output is-streaming" : undefined
                    }
                  >
                    <AiMarkdown>{answer}</AiMarkdown>
                    {isStreamPainting ? (
                      <span className="ai-stream-caret" aria-hidden="true" />
                    ) : null}
                  </div>
                ) : null}
              </div>
            </article>

            {showActions && (
              <AiResponseActions
                startActions={[
                  {
                    id: "copy",
                    label: t("contextMenu.copy"),
                    icon: (
                      <CopyIcon width={16} height={16} aria-hidden="true" />
                    ),
                    onClick: handleCopy,
                    disabled: isLoading || !answer,
                  },
                  {
                    id: "addNote",
                    label: t("contextMenu.addNote"),
                    icon: (
                      <NotesIcon width={16} height={16} aria-hidden="true" />
                    ),
                    onClick: () => showComingSoonNotification("add_note"),
                    muted: true,
                    title: t("contextMenu.comingSoon", "Coming soon"),
                  },
                ]}
                endActions={[
                  {
                    id: "regenerate",
                    label: t("readingAssistant.summary.regenerate"),
                    icon: <FaSyncAlt size={14} aria-hidden="true" />,
                    onClick: handleRegenerate,
                    disabled: isLoading,
                    busy: isLoading,
                  },
                ]}
              />
            )}
          </div>
        </div>
      </div>

      <div
        className="summary-type-actions"
        role="group"
        aria-label={t("readingAssistant.summary.typesLabel")}
      >
        <div
          className={`summary-drill-track${
            isChapterDrill ? " is-drilled" : ""
          }`}
        >
          <div
            className="summary-drill-panel summary-drill-panel--root"
            aria-hidden={isChapterDrill}
          >
            {SUMMARY_TYPES.map(({ id, drillsIn }) => {
              const isSelected = selectedType === id;
              return (
                <button
                  key={id}
                  type="button"
                  className={`summary-type-button${
                    isSelected ? " is-selected" : ""
                  }`}
                  aria-pressed={isSelected}
                  tabIndex={isChapterDrill ? -1 : 0}
                  onClick={() => handleSelectType(id)}
                  disabled={isLoading && selectedType !== id}
                >
                  <span>{t(`readingAssistant.summary.types.${id}`)}</span>
                  {drillsIn && (
                    <FaChevronRight
                      className="summary-type-chevron"
                      aria-hidden="true"
                    />
                  )}
                </button>
              );
            })}
          </div>

          <div
            className="summary-drill-panel summary-drill-panel--chapters"
            aria-hidden={!isChapterDrill}
          >
            <button
              type="button"
              className="summary-type-button summary-chapter-back-button"
              onClick={handleBackToTypes}
              tabIndex={isChapterDrill ? 0 : -1}
              aria-label={t("readingAssistant.summary.backToTypes")}
              title={t("readingAssistant.summary.backToTypes")}
            >
              <FaChevronLeft
                className="summary-type-chevron summary-type-chevron--left"
                aria-hidden="true"
              />
              <span>{t("readingAssistant.summary.types.chapter")}</span>
            </button>

            <div
              id="summary-chapter-list"
              className="summary-chapter-list"
              role="listbox"
              aria-label={t("readingAssistant.summary.chapterListLabel")}
            >
              {chapters.length === 0 ? (
                <p className="summary-chapter-empty">
                  {t("readingAssistant.summary.noChapters")}
                </p>
              ) : (
                chapters.map((chapter) => {
                  const isActive = chapter.id === selectedChapterId;
                  const isGenerating = isActive && isLoading;
                  return (
                    <button
                      key={chapter.id}
                      type="button"
                      role="option"
                      aria-selected={isActive}
                      tabIndex={isChapterDrill ? 0 : -1}
                      className={`summary-chapter-item${
                        isActive ? " is-active" : ""
                      }${isGenerating ? " is-loading" : ""}`}
                      onClick={() => handleSelectChapter(chapter)}
                      disabled={isLoading && !isActive}
                    >
                      <span className="summary-chapter-item-label">
                        {chapter.label}
                      </span>
                      {isGenerating ? (
                        <span
                          className="summary-chapter-spinner"
                          aria-hidden="true"
                        />
                      ) : (
                        <FaChevronRight
                          className="summary-chapter-item-chevron"
                          aria-hidden="true"
                        />
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SummaryTab;
